import { DimensionsType } from "@/components/ui-v2/AspectRatio/types";
import {
  getAreAspectRatiosEqual,
  getClosestToCurrentDimensions,
} from "@/components/ui-v2/AspectRatio/utils";
import { ReferenceImageType } from "@/components/ui-v2/References/types";
import { applyCustomSizeTarget } from "@/components/ui-v2/References/utils";
import { InferenceParameters, StyleCapabilities } from "@/types";
import { getImageObjectSafe } from "@/utils/images";

import { formatInferenceParamsForFormWithStyle } from "../../fields/Style/utils";
import {
  FORM_FIELDS_PROMPT_EDIT_IMAGE,
  FormTypePromptEditImage,
} from "./constants";

export const formatInferenceParamsForForm = async (
  params: InferenceParameters,
): Promise<Partial<FormTypePromptEditImage>> => {
  const outputForm: Partial<FormTypePromptEditImage> = {
    ...formatInferenceParamsForFormWithStyle(params),
  };

  if (params.files?.length) {
    const images = await Promise.all(
      params.files
        .filter((file) => file.type === "EDITING_REFERENCE")
        .map(async (file) => {
          const imageObject = await getImageObjectSafe(file.url);
          if (imageObject) {
            return {
              driveFileId: file.fileId,
              src: file.url,
              width: imageObject.width,
              height: imageObject.height,
              transform: file.transform,
            } as ReferenceImageType;
          }
          return null;
        }),
    );
    outputForm[FORM_FIELDS_PROMPT_EDIT_IMAGE.REFERENCES] = images.filter(
      (img): img is ReferenceImageType => img !== null,
    );
  }

  if (params.width && params.height) {
    outputForm[FORM_FIELDS_PROMPT_EDIT_IMAGE.DIMENSIONS] = [
      params.width,
      params.height,
    ];
  }

  if (typeof params.prompt !== "undefined") {
    outputForm[FORM_FIELDS_PROMPT_EDIT_IMAGE.PROMPT_TEXT] = params.prompt;
  }
  if (typeof params.promptJson !== "undefined") {
    outputForm[FORM_FIELDS_PROMPT_EDIT_IMAGE.PROMPT_JSON] = params.promptJson;
  }
  if (typeof params.promptLanguage !== "undefined") {
    outputForm[FORM_FIELDS_PROMPT_EDIT_IMAGE.PROMPT_LANGUAGE] =
      params.promptLanguage;
  }

  return outputForm;
};

export const getBestSupportedDimensions = (
  capabilities: StyleCapabilities,
  referenceDimensions: DimensionsType,
): DimensionsType => {
  if (!capabilities) {
    return referenceDimensions;
  }

  const aspectRatios = capabilities.imageToOutputAspectRatios ?? [];
  const exactRatio = aspectRatios.find((ratio) =>
    getAreAspectRatiosEqual([ratio.width, ratio.height], referenceDimensions),
  );

  if (
    !exactRatio &&
    capabilities.customSizeStep &&
    capabilities.customSizeTarget
  ) {
    return applyCustomSizeTarget(
      referenceDimensions,
      capabilities.customSizeTarget,
    );
  }

  return getClosestToCurrentDimensions(
    aspectRatios.map((ratio) => [ratio.width, ratio.height]),
    referenceDimensions,
  );
};
